/**
 * PauseMenu.tsx — WEB.4.PM (P0.2) / WEB.4.AR (Settings panel)
 *
 * Esc-triggered in-game menu. Actions: Resume, Restart Game, How to Play
 * (sub-overlay with rules summary), Settings (sub-overlay with audio volume
 * + language toggle).
 *
 * Esc keyboard handling is centralized HERE — the listener is always
 * attached. Gates:
 *   - Other modal open → Esc lets that modal close itself (we no-op)
 *   - HowToPlay or Settings sub-overlay open → Esc closes the sub-overlay first
 *   - PauseMenu open → Esc closes PauseMenu
 *   - Otherwise → Esc opens PauseMenu
 *
 * VISIBLE WHEN: store.paused === true.
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { setMasterVolume as setHowlerMasterVolume } from "../../audio/audio.js";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import { Tutorial } from "../Tutorial/Tutorial.js";
import styles from "./PauseMenu.module.css";

/**
 * Returns true when any other dialog / picker / banner is open. Esc should
 * fall through to that component's own close handler rather than open the
 * pause menu. Centralized here so we have ONE source of truth for "is the
 * user mid-interaction?"
 *
 * Includes a DOM check for `[data-testid="minimap-modal"]` because Minimap's
 * expanded state is component-local (not in the store). codex R2 HIGH:
 * without this, pressing Esc with the minimap modal open would close the
 * minimap AND open pause on the same key press.
 */
function _isAnyOtherModalOpen(s: ReturnType<typeof useGameStore.getState>): boolean {
  if (
    s.detailModalCard !== null ||
    s.buildMenuOpen === true ||
    s.targetPickerCard !== null ||
    s.tilePickerOpen === true ||
    s.tradingPromptOpen === true ||
    s.seatHandoffOpen === true ||
    s.gameStatus === "ended" ||
    s.lastImmunityBlock !== null ||
    s.lastThresholdEvent !== null ||
    s.lastStolenCard !== null ||
    // WEB.4.CS codex R1 HIGH: cutscene active = Esc must NOT open pause. The
    // CutscenePlayer's own capture-phase handler owns Esc here.
    s.cutsceneGroupId !== null
  ) {
    return true;
  }
  // Minimap expanded state lives in local React state — query DOM as the
  // single visible signal. Cheap (one selector) and runs only on Esc.
  if (typeof document !== "undefined") {
    if (document.querySelector('[data-testid="minimap-modal"]') !== null) return true;
  }
  return false;
}

export function PauseMenu(): React.ReactElement | null {
  const paused = useGameStore((s) => s.paused);
  const closePauseMenu = useGameStore((s) => s.closePauseMenu);
  const restartGame = useGameStore((s) => s.restartGame);
  const returnToMainMenu = useGameStore((s) => s.returnToMainMenu);
  const audioMuted = useGameStore((s) => s.audioMuted);
  const setAudioMuted = useGameStore((s) => s.setAudioMuted);
  const masterVolume = useGameStore((s) => s.masterVolume);
  const bgmVolume = useGameStore((s) => s.bgmVolume);
  const sfxVolume = useGameStore((s) => s.sfxVolume);
  const setMasterVolume = useGameStore((s) => s.setMasterVolume);
  const setBgmVolume = useGameStore((s) => s.setBgmVolume);
  const setSfxVolume = useGameStore((s) => s.setSfxVolume);
  const lang = useGameStore((s) => s.lang);
  const setLang = useGameStore((s) => s.setLang);
  const { t } = useI18n();

  // tutorialOpen: "How to Play" button opens the full-screen Tutorial overlay
  // (R15 Builder-AV) instead of the old inline text panel (R8 design).
  // Tutorial.onDismiss closes it and returns focus to pause buttons.
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const resumeBtnRef = useRef<HTMLButtonElement | null>(null);

  // R14 /goal HIGH fix: bridge store.masterVolume -> Howler master so slider
  // changes are audible. audioMuted overrides to silence; otherwise Howler's
  // master is the effective master (BGM/SFX per-channel volumes are persisted
  // for future per-channel wiring but currently not applied to Howler).
  useEffect(() => {
    if (audioMuted) {
      setHowlerMasterVolume(0);
    } else {
      setHowlerMasterVolume(masterVolume);
    }
  }, [masterVolume, audioMuted]);

  // codex R2 HIGH fix: attach in CAPTURE phase on window. Capture runs BEFORE
  // bubble, so this handler fires BEFORE the document-bound handlers in
  // CardDetailModal / BuildMenu / etc. When another modal is open we no-op
  // and let those bubble handlers do their close; we never see a stale
  // "no modal open" snapshot caused by a peer handler closing its own modal
  // in the same keydown.
  //
  // When pause IS open, we both `preventDefault` and `stopPropagation` so no
  // other handler tries to interpret the same Esc.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const s = useGameStore.getState();
      if (s.paused) {
        e.preventDefault();
        e.stopPropagation();
        // Tutorial overlay owns Esc via its own capture handler (Tutorial.tsx),
        // so we only need to handle non-tutorial sub-overlays here.
        if (settingsOpen) {
          setSettingsOpen(false);
          return;
        }
        s.closePauseMenu();
        return;
      }
      // Pause is down: open ONLY when no other modal owns Esc. The capture-
      // phase listener runs before bubble peers, so the state we read here
      // hasn't yet been mutated by their handlers.
      if (_isAnyOtherModalOpen(s)) return;
      // R9 codex LOW 5: only allow pause Esc during actual gameplay. In
      // menu / char-select, Esc should NOT open the gameplay pause overlay.
      if (s.gamePhase !== "playing") return;
      e.preventDefault();
      s.openPauseMenu();
    }
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [settingsOpen]);

  // Focus Resume button when pause opens (codex SH R1 a11y parity). The
  // close-buttons explicitly reset tutorial/settings state so we DON'T tie that to
  // the `paused` effect — closing pause via store action while htp is open
  // would otherwise rely on render timing that React 18's batching makes
  // unreliable.
  useEffect(() => {
    if (!paused) return;
    const id = window.setTimeout(() => {
      resumeBtnRef.current?.focus();
    }, 0);
    return () => {
      window.clearTimeout(id);
    };
  }, [paused]);

  if (!paused) return null;

  return (
    <>
      {tutorialOpen && (
        <Tutorial
          onDismiss={() => {
            // User watched/skipped tutorial from pause. setTutorialShown() is
            // called inside Tutorial.handleDismiss already; just close here.
            setTutorialOpen(false);
          }}
        />
      )}
      <dialog
        open
        className={styles.overlay}
        data-testid="pause-overlay"
        aria-labelledby="pause-title-text"
      >
        <div className={styles.panel}>
          <div id="pause-title-text" className={styles.title} data-testid="pause-title">
            {t("pause.title")}
          </div>

          {!settingsOpen && (
            <div className={styles.buttonStack} data-testid="pause-buttons">
              <button
                ref={resumeBtnRef}
                type="button"
                className={styles.btn}
                onClick={() => {
                  setSettingsOpen(false);
                  closePauseMenu();
                }}
                data-testid="pause-btn-resume"
              >
                {t("pause.resume")}
              </button>
              <button
                type="button"
                className={styles.btn}
                onClick={() => {
                  setSettingsOpen(false);
                  restartGame();
                }}
                data-testid="pause-btn-restart"
              >
                {t("pause.restartGame")}
              </button>
              <button
                type="button"
                className={styles.btn}
                onClick={() => {
                  setSettingsOpen(false);
                  returnToMainMenu();
                }}
                data-testid="pause-btn-main-menu"
              >
                {t("pause.mainMenu")}
              </button>
              <button
                type="button"
                className={styles.btn}
                onClick={() => setTutorialOpen(true)}
                data-testid="pause-btn-how-to-play"
              >
                {t("pause.howToPlay")}
              </button>
              <button
                type="button"
                className={styles.btn}
                onClick={() => setSettingsOpen(true)}
                data-testid="pause-btn-settings"
              >
                {t("pause.settings")}
              </button>
              <button
                type="button"
                className={styles.btn}
                onClick={() => {
                  window.open("https://wattsandwealth.online/feedback", "_blank", "noopener");
                }}
                data-testid="pause-btn-feedback"
                title={t("pause.feedbackTitle")}
              >
                {t("pause.feedback")}
              </button>
            </div>
          )}

          {settingsOpen && (
            <div className={styles.settingsPanel} data-testid="pause-settings-panel">
              <h2 className={styles.settingsHeader}>{t("settings.title")}</h2>
              <div className={styles.settingsBody}>
                {/* Mute toggle */}
                <div className={styles.settingsRow}>
                  <label className={styles.settingsLabel} htmlFor="pause-settings-mute">
                    {t("settings.soundsMuted")}
                  </label>
                  <input
                    id="pause-settings-mute"
                    type="checkbox"
                    checked={audioMuted}
                    onChange={(e) => setAudioMuted(e.currentTarget.checked)}
                    data-testid="pause-settings-mute"
                  />
                </div>

                {/* Master volume */}
                <div className={styles.settingsRow}>
                  <label className={styles.settingsLabel} htmlFor="pause-settings-master">
                    {t("settings.masterVolume")}
                  </label>
                  <div className={styles.sliderRow}>
                    <input
                      id="pause-settings-master"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round(masterVolume * 100)}
                      onChange={(e) => setMasterVolume(Number(e.currentTarget.value) / 100)}
                      data-testid="pause-settings-master-vol"
                      disabled={audioMuted}
                    />
                    <span className={styles.sliderValue} data-testid="pause-settings-master-val">
                      {Math.round(masterVolume * 100)}%
                    </span>
                  </div>
                </div>

                {/* BGM volume */}
                <div className={styles.settingsRow}>
                  <label className={styles.settingsLabel} htmlFor="pause-settings-bgm">
                    {t("settings.bgmVolume")}
                  </label>
                  <div className={styles.sliderRow}>
                    <input
                      id="pause-settings-bgm"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round(bgmVolume * 100)}
                      onChange={(e) => setBgmVolume(Number(e.currentTarget.value) / 100)}
                      data-testid="pause-settings-bgm-vol"
                      disabled={audioMuted}
                    />
                    <span className={styles.sliderValue} data-testid="pause-settings-bgm-val">
                      {Math.round(bgmVolume * 100)}%
                    </span>
                  </div>
                </div>

                {/* SFX volume */}
                <div className={styles.settingsRow}>
                  <label className={styles.settingsLabel} htmlFor="pause-settings-sfx">
                    {t("settings.sfxVolume")}
                  </label>
                  <div className={styles.sliderRow}>
                    <input
                      id="pause-settings-sfx"
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Math.round(sfxVolume * 100)}
                      onChange={(e) => setSfxVolume(Number(e.currentTarget.value) / 100)}
                      data-testid="pause-settings-sfx-vol"
                      disabled={audioMuted}
                    />
                    <span className={styles.sliderValue} data-testid="pause-settings-sfx-val">
                      {Math.round(sfxVolume * 100)}%
                    </span>
                  </div>
                </div>

                {/* Language toggle */}
                <div className={styles.settingsRow}>
                  <span className={styles.settingsLabel}>{t("settings.language")}</span>
                  <div className={styles.langToggle}>
                    <button
                      type="button"
                      className={`${styles.langBtn} ${lang === "en" ? styles.langBtnActive : ""}`}
                      onClick={() => setLang("en")}
                      data-testid="pause-settings-lang-en"
                      aria-pressed={lang === "en"}
                    >
                      {t("settings.languageEn")}
                    </button>
                    {/* Thai not localized yet — disabled "coming soon" (matches MainMenu). */}
                    <button
                      type="button"
                      className={styles.langBtn}
                      disabled
                      aria-disabled="true"
                      data-testid="pause-settings-lang-th"
                      title="Thai language — coming soon"
                      style={{ opacity: 0.4, cursor: "not-allowed" }}
                    >
                      {t("settings.languageTh")} · soon
                    </button>
                  </div>
                </div>
              </div>

              {/* R17 facilitator pilot data export — clipboard copy of JSONL
                  analytics sessions. Logs count + warns when >40 sessions. */}
              <div className={styles.settingsRow} data-testid="pause-settings-analytics-row">
                <span className={styles.settingsLabel}>Analytics</span>
                <button
                  type="button"
                  className={styles.btn}
                  onClick={async () => {
                    const data = useGameStore.getState().exportAnalyticsData();
                    const lines = data.split("\n").filter(Boolean).length;
                    if (typeof navigator !== "undefined" && navigator.clipboard) {
                      try {
                        await navigator.clipboard.writeText(data);
                        const warn = lines > 40 ? ` (⚠ near cap 50)` : "";
                        // biome-ignore lint/suspicious/noConsole: facilitator-only feedback
                        console.info(`[analytics] Exported ${lines} sessions${warn}`);
                      } catch {
                        // biome-ignore lint/suspicious/noConsole: fallback path
                        console.info(`[analytics] Clipboard unavailable; sessions: ${data}`);
                      }
                    }
                  }}
                  data-testid="pause-settings-export-analytics"
                >
                  Export sessions (clipboard)
                </button>
              </div>

              <button
                type="button"
                className={styles.btn}
                onClick={() => setSettingsOpen(false)}
                data-testid="pause-settings-close"
              >
                {t("settings.back")}
              </button>
            </div>
          )}

          <div className={styles.hint} aria-hidden="true">
            {settingsOpen ? t("pause.pressEscBack") : t("pause.pressEscResume")}
          </div>
        </div>
      </dialog>
    </>
  );
}
